import React, { useCallback, useState } from 'react';
import AdminPage from '../../components/admin/AdminPage';
import AdminUploadFile from '../../components/admin/AdminUploadFile';
import { Button, Col, FormGroup, Input, Row } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import useAuth from '../../hooks/useAuth';
import { AdminAlerts } from '../../models/alertModel';
import { fetchResponseAdapter } from '../../adapters/fetchAdapter';
import {
  updateSigningPromiseDateAtEndpoint,
  uploadSigningPromiseDocsToModelUnit,
} from './services/investorService';

const ModelUnitSigningPromiseDocs: React.FC = () => {
  const { id: modelId = '', modelUnitId = '' } = useParams();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const navigate = useNavigate();

  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const [alert, setAlert] = useState<AdminAlerts | undefined>(undefined);
  const [signingPromiseDateAt, setSigningPromiseDateAt] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<Array<any>>([]);

  const maxFileSizeMB = 25;

  const showWarning = useCallback((text: string) => {
    setAlert({
      success: false,
      warning: true,
      title: 'Error',
      text,
      onConfirm: () => setAlert(undefined),
    });
  }, []);

  const saveSigningPromise = useCallback(
    async (files: Array<any>) => {
      if (!signingPromiseDateAt) {
        showWarning('Debes de seleccionar la fecha de firma');
        return;
      }
      if (!files.length) {
        showWarning('Debes de adjuntar el documento de promesa de pago');
        return;
      }

      const formData = new FormData();
      files.forEach((file) => formData.append('signingPromiseDoc', file));

      const uploadResult = await callEndpoint(
        uploadSigningPromiseDocsToModelUnit(
          modelId,
          modelUnitId,
          formData,
          token,
        ),
      );
      if (uploadResult.status !== 200) {
        const response = fetchResponseAdapter(uploadResult.data);
        showWarning(response.message || 'Error al subir el documento');
        return;
      }

      const dateResult = await callEndpoint(
        updateSigningPromiseDateAtEndpoint(
          modelId,
          modelUnitId,
          { signingPromiseDocAt: signingPromiseDateAt },
          token,
        ),
      );
      if (dateResult.status !== 200) {
        const response = fetchResponseAdapter(dateResult.data);
        showWarning(response.message || 'Error al actualizar la fecha de firma');
        return;
      }

      setAlert({
        success: true,
        warning: false,
        title: 'Éxito',
        text: 'Actualizado correctamente',
        onConfirm: () => navigate(`/investors/models/${modelId}/model-units`),
      });
    },
    [callEndpoint, modelId, modelUnitId, navigate, token, signingPromiseDateAt, showWarning],
  );

  const pageName = 'Promesa de pago';
  const pageParentName = 'Preventas';

  return (
    <AdminPage
      name={pageName}
      parentName={pageParentName}
      globalAlertOptions={alert}
    >
      <Row>
        <Col>
          <FormGroup>
            <label className="form-control-label" htmlFor="signingPromiseDate">
              Fecha de firma
            </label>
            <Input
              id="signingPromiseDate"
              type={'date'}
              lang={'es'}
              value={signingPromiseDateAt}
              onChange={(event) => setSigningPromiseDateAt(event.target.value)}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          <AdminUploadFile
            handleUpload={saveSigningPromise}
            description={'Subir promesa de pago'}
            dropZoneText={`Máximo ${maxFileSizeMB}MB por archivo.`}
            maxFiles={1}
            maxSizeMB={maxFileSizeMB}
            onFileRejected={showWarning}
            hideSubmitButton
            onFilesChange={setSelectedFiles}
          />
        </Col>
      </Row>
      <Button
        color="primary"
        disabled={isLoading}
        onClick={() => saveSigningPromise(selectedFiles)}
      >
        <i className="fa fa-duotone fa-upload mr-1" /> Subir y actualizar
      </Button>
    </AdminPage>
  );
};

export default ModelUnitSigningPromiseDocs;
